import { useState, useCallback } from "react";
import { useToastStore } from "@/shared/stores/toastStore";
import { ApiTable } from "@/shared/api/table/api-table";
import { extractGoogleSheetId, isValidTableId } from "@/shared/lib/tableUtils";
import {
  classifyApiError,
  ApiErrorType,
  getUserFriendlyMessage,
} from "@/shared/lib/apiErrorHandler";
import { useServiceAccountStore } from "@/shared/stores/serviceAccountStore";
import { useServiceWorkflowStore } from "@/shared/stores/serviceWorkflowStore";
import { useTableStore } from "@/shared/stores/tableStore";

interface UseTableConnectionProps {
  onSuccess: (tableId: string) => void;
  onError: (message: string) => void;
}

export const useTableConnection = ({
  onSuccess,
  onError,
}: UseTableConnectionProps) => {
  const { pId, serviceType } = useServiceWorkflowStore();
  const { selectedNms } = useTableStore();
  const { showToast } = useToastStore();
  const { serviceAccount } = useServiceAccountStore();
  const [isConnecting, setIsConnecting] = useState(false);
  const [tableUrl, setTableUrl] = useState("");

  const handleConnectTable = useCallback(async () => {
    const createdTableId = extractGoogleSheetId(tableUrl);

    if (!createdTableId || !isValidTableId(createdTableId)) {
      onError("Неверный формат ссылки на таблицу.");
      return;
    }

    if (!serviceAccount) {
      onError("Данные сервисного аккаунта не загружены.");
      return;
    }

    if (!pId) {
      onError("Не подключен токен пользователя.");
      return;
    }

    setIsConnecting(true);

    try {
      const tableApi = new ApiTable();

      let accessData;
      try {
        const accessResponse = await tableApi.checkTableAccess(
          serviceAccount.client_id,
          createdTableId,
        );
        accessData = accessResponse.data;
      } catch (accessError) {
        setIsConnecting(false);
        const classified = classifyApiError(accessError);

        if (classified.type === ApiErrorType.ACCESS_DENIED) {
          onError(
            getUserFriendlyMessage(classified, { email: serviceAccount.email }),
          );
          return;
        }

        onError(getUserFriendlyMessage(classified));
        return;
      }

      if (!accessData.has_access) {
        setIsConnecting(false);
        onError(
          `Предоставьте доступ к таблице для email: ${serviceAccount.email}`,
        );
        return;
      }

      try {
        if (serviceType === "auto_jam") {
          await tableApi.createJamTable({
            table_id: createdTableId,
            client_id: serviceAccount.client_id,
            supplier_id: pId,
            nms: selectedNms || [],
          });
        } else {
          await tableApi.createAutoTable({
            table_id: createdTableId,
            client_id: serviceAccount.client_id,
            supplier_id: pId,
          });
        }

        showToast(
          "Таблица успешно добавлена. Данные загружаются. Сообщим о готовности в боте (до 30 мин).",
          "success",
        );
        onSuccess(createdTableId);
      } catch (createError) {
        setIsConnecting(false);
        const classified = classifyApiError(createError);

        if (classified.type === ApiErrorType.RESOURCE_CONFLICT) {
          onError("Эта таблица уже подключена к системе");
          return;
        }

        if (classified.type === ApiErrorType.SERVER_ERROR) {
          onError(
            "Сервер временно недоступен. Попробуйте через несколько минут или обратитесь в поддержку",
          );
          return;
        }

        onError(
          getUserFriendlyMessage(classified) ||
            "Не удалось создать таблицу. Попробуйте еще раз",
        );
      }
    } catch (unexpectedError) {
      setIsConnecting(false);
      console.error("Unexpected error in handleConnectTable:", unexpectedError);
      onError("Произошла непредвиденная ошибка. Попробуйте еще раз");
    } finally {
      setIsConnecting(false);
    }
  }, [
    tableUrl,
    serviceAccount,
    pId,
    serviceType,
    selectedNms,
    onSuccess,
    onError,
    showToast,
  ]);

  return {
    isConnecting,
    tableUrl,
    setTableUrl,
    handleConnectTable,
  };
};
